import { useMemo } from 'react';
import { useEquippedCosmetics } from '@kidshome/api-contract';
import { equippedLook, type EquippedLook } from '@/entities/cosmetics/cosmetics-theme';

/**
 * Resolve a child's equipped cosmetic ids (per slot) for the visuals. Always returns a usable shape
 * (all-null while loading / on error / when nothing is equipped) so the companion/avatar fall back to
 * the DEFAULT look and never block or nag.
 *
 * `childId` is the guardian path: `GET /v1/cosmetics/equipped` accepts `?childId=` so a guardian can
 * view a child's look (the endpoint is `cosmetics:read`-gated; BOLA in the handler restricts a child
 * to their own set). Omit it (or pass null) for the self view. Callers gate the fetch with `enabled`
 * — for a self view that means "the signed-in user is a child" (every child holds `cosmetics:read`;
 * a purchased + equipped look must keep rendering even when `cosmetic:purchase` was never granted or
 * is later revoked — losing a paid-for look would read as punishment).
 */
export function useEquippedLook(options?: {
  enabled?: boolean;
  childId?: string | null;
}): EquippedLook {
  const equipped = useEquippedCosmetics(options?.childId ?? null, {
    enabled: options?.enabled ?? true,
  });
  // TanStack Query keeps `data` referentially stable (structural sharing) until the equipped set actually
  // changes, so memoizing on it gives a stable `EquippedLook` reference. Without this, every render produced
  // a fresh object, defeating downstream `React.memo` on the (heavy) hero figure (Plan D).
  return useMemo(() => equippedLook(equipped.data), [equipped.data]);
}
